'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import CloseIcon from '../Icons/CloseIcon';
import { lookupZipInServiceArea } from '@/lib/actions';
import {
  HeaderLocationData,
  HeaderLocationDrawerProps,
  LocationResultCardProps,
  LookupState,
  ZipCodeLookupFieldProps,
} from './types';
import LocationResultArrowIcon from '../Icons/LocationResultArrowIcon';
import LocationResultPinIcon from '../Icons/LocationResultPinIcon';
import SuccessStatusIcon from '../Icons/SuccessStatusIcon';
import ErrorStatusIcon from '../Icons/ErrorStatusIcon';
import { buttonBase, buttonSizes, buttonVariants } from '../Button/buttonStyles';

function ZipCodeLookupField({
  zip,
  status,
  onZipChange,
  onKeyDown,
  placeholder = 'Enter your zip code',
  emphasizePlaceholder = false,
  showTrailingPlaceholderPipe = false,
}: ZipCodeLookupFieldProps) {
  const isSearched = status !== 'idle';
  const showCustomPlaceholder = showTrailingPlaceholderPipe && !zip && !isSearched;

  return (
    <div className='relative w-full'>
      {isSearched && (
        <div className='absolute top-0 left-[11px] z-10 mt-[-2px] flex items-center bg-white px-[4px] py-[2px]'>
          <span className='font-instrument text-[10px] leading-0 tracking-[-0.01em] text-primary-500'>
            Enter your ZIP code
          </span>
        </div>
      )}

      <div
        className={`relative flex h-[50px] w-full items-center rounded-[8px] border-2 bg-white transition-colors duration-200 ${
          status === 'valid'
            ? 'border-[#16a085]'
            : status === 'invalid'
              ? 'border-[#e05252]'
              : 'border-[#e9e8e7]'
        } ${isSearched ? 'px-[0px] gap-0 mt-[8px]' : 'px-[16px] gap-[16px] mt-0'}`}
      >
        {isSearched && (
          <div className='flex h-full w-[48px] shrink-0 items-center justify-center'>
            {status === 'valid' && (
              <div className='flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#16a085]'>
                <SuccessStatusIcon />
              </div>
            )}
            {status === 'invalid' && (
              <div className='flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#e05252]'>
                <ErrorStatusIcon />
              </div>
            )}
          </div>
        )}

        {showCustomPlaceholder && (
          <div className='pointer-events-none absolute left-[18px] top-1/2 flex -translate-y-1/2 items-center gap-[6px]'>
            <span className='font-instrument text-[16px] leading-[150%] tracking-[-0.01em] text-primary-500'>
              {placeholder}
            </span>
            <span
              aria-hidden='true'
              className='text-[18px] leading-none text-secondary-500'
            >
              |
            </span>
          </div>
        )}

        <input
          type='text'
          inputMode='numeric'
          pattern='\d{5}'
          maxLength={5}
          value={zip}
          onChange={(e) => onZipChange(e.target.value)}
          onKeyDown={onKeyDown}
          autoComplete='off'
          autoCorrect='off'
          autoCapitalize='off'
          spellCheck={false}
          placeholder={showCustomPlaceholder ? undefined : isSearched ? undefined : placeholder}
          aria-label='Enter your ZIP code'
          className={`flex-1 bg-transparent outline-none focus:outline-none text-primary-500 font-instrument tracking-[-0.01em] leading-[150%] min-w-0 ${
            emphasizePlaceholder
              ? 'placeholder:text-primary-500'
              : 'placeholder:text-primary-500/40'
          } [&:-webkit-autofill]:shadow-[0_0_0_1000px_white_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(var(--primary-500))] ${
            isSearched ? 'text-[14px]' : 'text-[16px]'
          }`}
        />
      </div>
    </div>
  );
}

function LocationResultCard({ locality, href, onNavigate }: LocationResultCardProps) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      className='group flex items-center gap-3 rounded-lg px-4 py-3 transition-all duration-150 border-[1.5px] border-[#e8f5f2] bg-[#f6fdfb] hover:bg-[#e8f5f2] hover:border-[#16a085]'
    >
      <span className='flex shrink-0 items-center justify-center rounded-sm w-[28px] h-[28px] bg-[#16a085]'>
        <LocationResultPinIcon />
      </span>

      <span className='flex-1 font-instrument font-medium text-primary-500 text-[14px] tracking-[-0.01em]'>
        {locality}
      </span>

      <LocationResultArrowIcon className='shrink-0 stroke-secondary-500 transition-transform duration-150 group-hover:translate-x-0.5' />
    </Link>
  );
}

export default function HeaderLocationDrawer({
  isOpen,
  onClose,
  selectedLocation,
  onSave,
}: HeaderLocationDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const [zipInput, setZipInput] = useState('');
  const [lookupState, setLookupState] = useState<LookupState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingLocation, setPendingLocation] = useState<HeaderLocationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const savedLocationLabel = selectedLocation?.zip ? `ZIP: ${selectedLocation.zip}` : '';

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    setZipInput('');
    setLookupState('idle');
    setErrorMessage('');
    setPendingLocation(null);
    setIsLoading(false);

    const panel = panelRef.current;
    const focusableSelectors =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const focusableElements = panel
      ? Array.from(panel.querySelectorAll<HTMLElement>(focusableSelectors))
      : [];

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    firstElement?.focus();

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'Tab' && focusableElements.length > 0) {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleChange = (value: string) => {
    const normalized = value.replace(/\D/g, '').slice(0, 5);
    setZipInput(normalized);
    setLookupState('idle');
    setErrorMessage('');
    setPendingLocation(null);
  };

  const handleSearch = async () => {
    if (!zipInput || zipInput.length !== 5) {
      setLookupState('error');
      setErrorMessage('Please enter a valid 5-digit ZIP code.');
      setPendingLocation(null);
      return;
    }

    setIsLoading(true);
    setLookupState('idle');
    setErrorMessage('');
    setPendingLocation(null);

    try {
      const result = await lookupZipInServiceArea(zipInput);

      if (!result?.inServiceArea || !result.locality || !result.stateShort || !result.link) {
        setLookupState('error');
        setErrorMessage('Your home is not in our service area.');
        setPendingLocation(null);
        return;
      }

      setPendingLocation({
        zip: zipInput,
        locality: result.locality,
        stateShort: result.stateShort,
        link: result.link,
      });
      setLookupState('success');
    } catch (error) {
      console.error('[HeaderLocationDrawer] ZIP search failed:', error);
      setLookupState('error');
      setErrorMessage('Something went wrong while searching for your ZIP code.');
      setPendingLocation(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (pendingLocation) {
      onSave(pendingLocation);
      onClose();
      return;
    }

    await handleSearch();
  };

  const handleInputKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handlePrimaryAction();
    }
  };

  const buttonLabel = pendingLocation ? 'Update' : 'Search';

  const showSavedLocationPlaceholder =
    Boolean(selectedLocation) && !zipInput && lookupState === 'idle';

  return (
    <div
      aria-hidden={!isOpen}
      inert={!isOpen}
      className={`fixed inset-0 z-100 transition-opacity duration-300 ${
        isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      }`}
    >
      <button
        type='button'
        aria-label='Close location panel'
        onClick={onClose}
        className='absolute inset-0 bg-black/40 transition-opacity duration-300 cursor-pointer'
      />

      <div
        ref={panelRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby='location-drawer-title'
        className={`absolute right-0 top-0 flex h-full w-full max-w-[350px] transform flex-col overflow-y-auto bg-white px-6 py-7 shadow-xl transition-transform duration-300 ease-in-out md:rounded-l-2xl md:px-10 md:py-10 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          type='button'
          onClick={onClose}
          aria-label='Close location panel'
          className='ml-auto cursor-pointer'
        >
          <CloseIcon className='h-12 w-12 rounded-lg border-2 border-secondary-500' />
        </button>

        <div className='mt-16 flex w-full flex-col items-start gap-3'>
          <h2
            id='location-drawer-title'
            className='text-[20px] font-bold leading-[120%] tracking-[-0.2px] text-primary-500'
          >
            Set Your Location
          </h2>

          <p className='font-instrument text-[16px] font-normal leading-[130%] tracking-[-0.16px] text-primary-500'>
            Find the location right for you.
          </p>

          <div className='w-full pt-2'>
            <ZipCodeLookupField
              zip={zipInput}
              status={
                lookupState === 'success' ? 'valid' : lookupState === 'error' ? 'invalid' : 'idle'
              }
              onZipChange={handleChange}
              onKeyDown={handleInputKeyDown}
              placeholder={
                showSavedLocationPlaceholder ? savedLocationLabel : 'Enter your zip code'
              }
            />

            {lookupState === 'error' && (
              <p className='mt-3 text-[13px] leading-[140%] tracking-[-0.01em] text-primary-500'>
                {errorMessage}
              </p>
            )}

            {lookupState === 'success' && pendingLocation && (
              <div className='mt-3 w-full'>
                <p className='mb-3 text-[13px] leading-[140%] tracking-[-0.01em] text-primary-500'>
                  Your home is in our service area!
                </p>

                <LocationResultCard
                  locality={pendingLocation.locality}
                  href={`/locations/${pendingLocation.link}`}
                  onNavigate={onClose}
                />
              </div>
            )}
          </div>
        </div>

        <div className='mt-8 w-full'>
          <button
            type='button'
            onClick={handlePrimaryAction}
            disabled={isLoading}
            className={`
    ${buttonBase}
    ${buttonSizes.full}
    ${buttonVariants.primary}
    disabled:cursor-not-allowed
    disabled:opacity-70
  `}
          >
            {isLoading ? 'Searching...' : buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
